// ============================================================================
// MODULAR DESIGN PRINCIPLES (MANDATORY)
// ============================================================================
// This file strictly follows:
// 1. Single Responsibility Principle - Each component does ONE thing
// 2. Separation of Concerns - Logic separated from presentation
// 3. Modular Architecture - Independent, reusable components
//
// Refer to: docs/architecture/modular-design-standards.md
// ============================================================================

import React from 'react';
import {
  Paper,
  Box,
  Typography
} from '@mui/material';
// Recharts imports removed - using CSS-based chart instead

const ScoreDistributionChart = ({ data, totalRecords: propTotalRecords }) => {
  
  if (!data) {
    return (
      <Paper sx={{ p: 3, height: '100%' }}>
        <Typography variant="h6" gutterBottom>
          Score Distribution
        </Typography>
        <Typography variant="body2" color="text.secondary">
          No data available
        </Typography>
      </Paper>
    );
  }

  // Create score ranges with 5-point intervals (0-5, 5-10, 10-15, ..., 95-100)
  const scoreRanges = [];
  for (let i = 0; i <= 95; i += 5) {
    const start = i;
    const end = i + 5;
    const rangeKey = `${start}-${end}`;
    const value = data[rangeKey] || 0;
    
    let color = 'error';
    if (i >= 60) color = 'success';
    else if (i >= 40) color = 'primary';
    else if (i >= 20) color = 'warning';
    
    scoreRanges.push({
      range: rangeKey,
      label: `${start}-${end}`,
      value: value,
      color: color
    });
  }

  // Use prop totalRecords if available, otherwise calculate from data
  const calculatedTotalRecords = scoreRanges.reduce((sum, range) => sum + range.value, 0);
  const totalRecords = propTotalRecords !== undefined ? propTotalRecords : calculatedTotalRecords;
  const maxValue = Math.max(...scoreRanges.map(range => range.value));

  // Prepare data for Recharts
  const chartData = scoreRanges.map((range) => ({
    range: range.label,
    value: range.value,
    percentage: totalRecords > 0 ? (range.value / totalRecords) * 100 : 0,
    color: range.color
  }));
  

  const getColorValue = (color) => {
    const colorMap = {
      error: '#f44336',
      warning: '#ff9800',
      info: '#2196f3',
      primary: '#1976d2',
      success: '#4caf50'
    };
    return colorMap[color] || '#666';
  };

  // CustomTooltip removed - using CSS-based chart with title tooltips

  return (
    <Paper sx={{ p: 1, height: '100%', width: '100%', display: 'flex', flexDirection: 'column' }}>
      <Box sx={{ mb: 1 }}>
        <Typography variant="h6" gutterBottom>
          Score Distribution
        </Typography>
        <Typography variant="body2" color="text.secondary">
          QA scores distribution (0-100 range, 5-point intervals)
        </Typography>
      </Box>

      {/* Bar Chart */}
      <Box sx={{ flex: 1, minHeight: 0, width: '100%' }}>
        
        {/* Simple Bar Chart using CSS */}
        <Box sx={{ height: '100%', display: 'flex', flexDirection: 'column', justifyContent: 'flex-end' }}>
          <Box sx={{ display: 'flex', alignItems: 'end', height: '80%', gap: '2px', px: 1 }}>
            {chartData.map((item, index) => {
              const height = maxValue > 0 ? (item.value / maxValue) * 100 : 0;
              const color = item.value > 0 ? '#1976d2' : '#e0e0e0';
              
              return (
                <Box
                  key={index}
                  sx={{
                    flex: 1,
                    height: `${height}%`,
                    backgroundColor: color,
                    minHeight: item.value > 0 ? '4px' : '2px',
                    borderRadius: '2px 2px 0 0',
                    position: 'relative'
                  }}
                  title={`${item.range}: ${item.value} records`}
                />
              );
            })}
          </Box>
          
          {/* X-axis labels */}
          <Box sx={{ display: 'flex', justifyContent: 'space-between', px: 1, mt: 1, fontSize: '10px' }}>
            {chartData.map((item, index) => (
              <Box
                key={index}
                sx={{
                  flex: 1,
                  textAlign: 'center',
                  transform: 'rotate(-45deg)',
                  transformOrigin: 'center',
                  fontSize: '9px',
                  color: 'text.secondary'
                }}
              >
                {item.range}
              </Box>
            ))}
          </Box>
        </Box>
      </Box>

      {/* Summary */}
      <Box sx={{ mt: 1, p: 1, bgcolor: 'grey.50', borderRadius: 1 }}>
        <Typography variant="body2" color="text.secondary" align="center">
          Total Records: <strong>{totalRecords}</strong> | 
          <Box component="span" sx={{ 
            fontSize: '1.1em', 
            fontWeight: 'bold', 
            color: 'text.primary',
            ml: 0.5
          }}>
            Average Score: {totalRecords > 0 ? 
              (scoreRanges.reduce((sum, range) => {
                const midPoint = (parseInt(range.range.split('-')[0]) + parseInt(range.range.split('-')[1])) / 2;
                return sum + (range.value * midPoint);
              }, 0) / totalRecords).toFixed(1)
              : '0.0'
            }
          </Box>
        </Typography>
      </Box>
    </Paper>
  );
};

export default ScoreDistributionChart;
